import { MealFirstServiceSsrEnum, MealSecondServiceSsrEnum, MealServiceType } from "@constants/meals.constants";
import { PassengerTypeCode } from "@models/booking.models";
import { MealsSsrUpdate } from "@models/cms/meals.model";
import { MealPassengerOption, MealPassenger, MealLeg, JourneyMeal, JourneyMealCalculationPayload, SelectPassengerMealPayload, JourneyMealLegCalculationPayload, JourneyMealAddPayload } from "@models/meals.model";
import { N0SsrAvailability } from "@models/responses/ssr-availability-response.model";


export const createJourneyMeal = (payload: JourneyMealCalculationPayload): JourneyMeal =>  {
  return {
    serviceType: payload.serviceType,
    mealTitle: payload.mealTitle,
    serveTime: payload.serveTime,
    image: payload.image,
    imageText1: payload.mealLine1,
    imageText2: payload.mealLine2,
    mealDescription: payload.mealInfo,
    mealAddInnerHTML:payload.mealAddHtml,
    mealAddPrice: readMinAddPrice(payload.ssrAvailability, payload.mealSsrs),
    legs: payload.mealLegs,
    isCollapsed: !payload.mealLegs.some(l => l.passengers.some(p => p.included || p.paid || !!p.selectedSsr.ssrCode)),
    collapseAllowed: !payload.mealLegs.some(l => l.passengers.some(p => p.included || p.paid)),
    sameMealForReturnFlight: !payload.ssrSellError && hasSameMealForReturnFlight(payload.mealLegs),
    sameMealForReturnFlightPossible: !payload.ssrSellError && isPossibleToSelectSameMeal(payload.mealLegs),
    buttonAddMeal: payload.buttonAddMeal,
    buttonRemoveMeal: payload.buttonRemoveMeal
  };
}

export const createMealLegs = (
  payload: JourneyMealLegCalculationPayload
) => {
  return payload.mealSelection.map(m => {
    const leg = payload.journeyLegs.find(jl => jl.legKey === m.legKey);
    return {
      legKey: m.legKey,
      origin: leg?.designator.origin,
      destination: leg?.designator.destination,
      canSellMeals: m.legKey ? payload.canSellMeals[m.legKey] : false,
      passengers: m.passengers.map(p => {
        const passengerSSr = p.ssrs.find(f =>f.serviceType === payload.mealServiceType)
        const passenger = payload.passengers[p.passengerKey];
        const mealPassenger = {
          key: p.passengerKey,
          type: passenger?.passengerTypeCode,
          name: passenger?.name,
          included:  passengerSSr?.included ?? false,
          paid: passengerSSr?.paid ?? false,
          price: passengerSSr?.price ?? 0,
          priceFromAllLegs: payload.mealSelection.flatMap(ms => ms.passengers).filter(msp => msp.passengerKey === p.passengerKey).flatMap(msp => msp.ssrs.filter(ssr => ssr.serviceType === payload.mealServiceType)).reduce((total, ssr) => total + ssr.price, 0),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          selectedSsr: passengerSSr ? { ssrCode: passengerSSr.ssrCode, ssrName: payload.mealService[passengerSSr.ssrCode] } : { ssrCode: null, ssrName: payload.noMeal },
          options: payload.ssrAvailability.legsSsrs.find(f => f.legKey === m.legKey)?.mealSsrs.filter(f => payload.mealSsrCodes.some(m => m === f.ssrCode)).map(m => {
            return {
              available: m.available,
              ssrCode: m.ssrCode,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ssrName: payload.mealService[m.ssrCode],
              price: m.passengersAvailability.find(f => f.passengerKey === p.passengerKey)?.price
            } as MealPassengerOption
          }) ?? []
        } as MealPassenger
        if ((!mealPassenger.included || mealPassenger.paid) && mealPassenger.options) {
          mealPassenger.options.unshift({ ssrCode: null, ssrName: payload.noMeal, price: 0 })
        }
        return mealPassenger;
      })
    } as MealLeg;
  })
}

export const getPassengerSelectedSsrs = (ssrs: MealsSsrUpdate[], mealPayload: SelectPassengerMealPayload) => {
  const { mealPassenger, serviceType: mealServiceType } = mealPayload;
  const selectedSsrCode = mealPassenger.selectedSsr.ssrCode;

  if (!selectedSsrCode) {
    return ssrs.filter(s => s.serviceType !== mealServiceType);
  }

  const ssrIndex = ssrs.findIndex(s => s.serviceType === mealServiceType);
  if (ssrIndex !== -1) {
    return ssrs.map((s, index) => index === ssrIndex ? { ...s, ssrCode: selectedSsrCode } : s);
  } else {
    return [
      ...ssrs,
      {
        serviceType: mealServiceType,
        ssrCode: selectedSsrCode,
        count: 1,
        included: mealPassenger.included,
        paid: mealPassenger.paid,
        price: mealPassenger.price ?? 0
      }
    ];
  }
};

export const mergeSsrCodes = (ssrs: MealsSsrUpdate[], ssr: MealsSsrUpdate) => {
  const index = ssrs.findIndex(existingSsr => existingSsr.ssrCode === ssr.ssrCode);
  if (index === -1) {
    return [...ssrs, ssr];
  }
  return ssrs;
}

export const getPassengerDefaultMealSsr = (serviceType: MealServiceType) => {
    if (serviceType === MealServiceType.FIRST_SERVICE) {
      return MealFirstServiceSsrEnum.PDML;
    }
    return MealSecondServiceSsrEnum.SNML;
};



const readMinAddPrice = (ssrAvailability: N0SsrAvailability, mealSsrs: MealFirstServiceSsrEnum[] | MealSecondServiceSsrEnum[]) => {
  const prices = ssrAvailability.legsSsrs
    .flatMap(f => f.mealSsrs)
    .filter(f => mealSsrs.some(m => m === f.ssrCode))
    .flatMap(f => f.passengersAvailability).map(m => m.price).filter(p => p > 0);
    if (prices. length > 0) {
      return Math.min(...prices);
    }
  return 0;
}

const hasSameMealForReturnFlight = (legs: MealLeg[]) => {
  if (legs[0] && legs[1]) {

    if (legs.slice(1).some(l => l.passengers.some(p => p.included))){
      return false;
    }

    const firstLegSsrCodes = legs[0].passengers.map(passenger => passenger.selectedSsr);
    const secondLegSsrCodes = legs[1].passengers.map(passenger => passenger.selectedSsr);
    if (firstLegSsrCodes.length !== secondLegSsrCodes.length) {
      return false;
    }

    for (let i = 0; i < firstLegSsrCodes.length; i++) {
      if (firstLegSsrCodes[i]?.ssrCode !== secondLegSsrCodes[i]?.ssrCode) {
        return false;
      }
    }
    return true;
  }
  return false;
}


const isPossibleToSelectSameMeal = (legs: MealLeg[]) => {
  return legs.length > 1 && legs.every(l => l.passengers.every(p => !p.included))
}
